<template>
    <form :action="company.buildCompanyRoute('zones/'+this.zone.ID)" method="post">
        <input name="_method" type="hidden" value="PATCH">
        <input type="hidden" name="_token" :value="csrf" />

        <div v-if="permissions.isPartner()">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="px-4 sm:px-0">
                        <span class="text-lg font-medium leading-6 text-gray-900">Zone Placement</span>
                        <p class="mt-1 text-sm leading-5 text-gray-600">
                            Use this to move the Zone to a separate Area
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white sm:p-6">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <label class="block text-sm font-medium leading-5 text-gray-700">Zone Placement</label>
                                    <select2
                                        :options="areas"
                                        :multiple="false"
                                        :value="zone.AreaID"
                                        name="new_area"
                                        :close-on-select="true"
                                        placeholder="Select an Area"
                                        class="w-full"
                                    >
                                    </select2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <div v-if="permissions.isSonicu() || permissions.isSonicuViewOnly()" class="hidden sm:block">
            <div class="py-5">
                <div class="border-t border-gray-200"></div>
            </div>
        </div>

        <div class="mt-10 sm:mt-0">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="px-4 sm:px-0">
                        <span class="text-lg font-medium leading-6 text-gray-900">Points</span>
                        <p class="mt-1 text-sm leading-5 text-gray-600">
                            Manage your zone by renaming it <i>or</i> by adding points to the zone.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white sm:p-6">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6 sm:col-span-6">
                                    <label for="zone_name" class="block text-sm font-medium leading-5 text-gray-700">Zone Name</label>
                                    <input id="zone_name"
                                           name="name"
                                           type="text"
                                           v-model="this.zone.Name"
                                           class="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-200 focus:border-blue-300 sm:text-sm sm:leading-5">
                                </div>
                                <div class="col-span-6">
                                    <label for="zone" class="block text-sm font-medium leading-5 text-gray-700">Points</label>
                                    <select2 :options='allowedPoints'
                                             :close-on-select="false"
                                             :allow-clear="true"
                                             :multiple="true"
                                             :value='metersSelectedOnZone'
                                             id="meters[]"
                                             name="meters[]"
                                             placeholder="Select points for this zone"
                                             class="p-2 m-2"
                                    ></select2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="hidden sm:block">
            <div class="py-5">
                <div class="border-t border-gray-200"></div>
            </div>
        </div>

        <div class="mt-10 sm:mt-0">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="px-4 sm:px-0">
                        <span class="text-lg font-medium leading-6 text-gray-900">Groups & Alarming</span>
                        <p class="mt-1 text-sm leading-5 text-gray-600">
                            You can change your alarm settings and add groups to this zone. Every user in a group assigned here will be able to view all points.
                        </p>
                        <p v-if="groupAlarmsEnabled"
                          class="mt-1 text-sm leading-5 text-gray-600">
                            When you <i>Enable Alarming</i> for a group, all users in that group who have alarms enabled will then be eligible
                          to receive alarms (if they have alarms enabled). This greatly simplifies alarming for large groups.
                        </p>
                      <template v-if="permissions.isPermissionsLoaded()">
                        <div v-if="(! permissions.can('view_all_areas')) && ! isEditingUserInAnySelectedGroups"
                             class="rounded-md bg-yellow-50 p-4">
                          <div class="flex">
                            <div class="flex-shrink-0">
                              <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                              </svg>
                            </div>
                            <div class="ml-3">
                              <span class="text-sm font-medium text-yellow-800">Attention needed</span>
                              <div class="mt-2 text-sm text-yellow-700">
                                <p>Please make sure to select at least one group that you're a member of. This way, you'll be able to see this Zone after saving your changes.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow overflow-hidden sm:rounded-md">
                        <div class="px-4 py-5 bg-white sm:p-6">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <label class="block text-sm font-medium leading-5 text-gray-700">Users to Receive Alarms</label>
                                    <select2
                                        :options="allUsersWithNotificationsEnabled"
                                        :multiple="true"
                                        :value="userIdsToNotifyOnZone"
                                        name="users[]"
                                        :close-on-select="false"
                                        placeholder="Select users to notify of alarms"
                                        class="w-full"
                                    >
                                    </select2>
                                </div>
                                <div class="col-span-6">
                                    <label class="block text-sm font-medium leading-5 text-gray-700">Groups</label>
                                    <select2
                                        v-model:value="groupIdsSelectedOnZone"
                                        :options="allGroupsObjectForSelect"
                                        :multiple="true"
                                        :value="groupIdsSelectedOnZone"
                                        name="groups[]"
                                        :close-on-select="false"
                                        placeholder="Select Groups"
                                        class="w-full"
                                    >
                                    </select2>
                                </div>
                              <div class="col-span-6">
                                <ul v-if="groupAlarmsEnabled"
                                    role="list"
                                    class="grid grid-cols-1 gap-6 xl:grid-cols-2">
                                  <div
                                    v-for="group in allGroups"
                                    v-show="groupEnabledOnZone(group.ID)">
                                    <alarm-settings-edit-groups-card
                                      :group="group"
                                      :alarms-enabled-prop="groupAlarmsEnabledOnZone(group.ID)">
                                    </alarm-settings-edit-groups-card>
                                  </div>
                                </ul>
                              </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="hidden sm:block">
            <div class="py-5">
                <div class="border-t border-gray-200"></div>
            </div>
        </div>

        <div class="flex justify-between">
            <button @click="askToDelete"
                    v-if="permissions.can('delete_zone')"
                    type="button"
                   value="Delete Zone"
                   class="bg-white hover:bg-red-600 text-red-700 hover:text-white border-red-200 hover:border-red-400 active:border-red-600
               w-1/4 mt-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none"
            >
                Delete Zone
            </button>

            <input type="submit"
                   name="submit"
                   value="Save Zone"
                   class="bg-blue-600 hover:bg-blue-500 focus:ring-blue-200 focus:bg-blue-500 active:bg-blue-600
               w-1/2 mt-4 py-2 border border-transparent text-sm font-medium rounded-md text-white shadow-sm focus:outline-none"
            >
        </div>

        <!--    Notification modal for deletion, if needed -->
        <notification-modal
            v-model:is-open="showConfirmModal"
            :give-option=true
            :is-error-modal="true"
            v-on:action="trash"
        >
            <template #title-content>
                Are you sure?
            </template>
            Would you really like to delete this Zone? This will change notification settings for users and move points to default zones
        </notification-modal>



    </form>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import axios from "axios";
import ButtonRedSecondary from "@/components/elements/buttons/ButtonRedSecondary";
import NotificationModal from "@/components/elements/NotificationModal";
import AlarmSettingsEditGroupsCard from "@/components/AlarmSettingsEditGroupsCard";

export default {
    name: "zoneEdit",
    components: {ButtonRedSecondary, NotificationModal, AlarmSettingsEditGroupsCard},
    mixins: [SonicuCoreMixin],
    props: {
        zone: {
            type: Object,
            required: true,
        },
        allowedPoints: {
            type: Object,
            required: true,
        },
        groupsOnZoneProp: {
            type: Object,
            required: true,
        },
        allGroups: {
            type: Array,
            required: true,
        },
        allUsersWithNotificationsEnabled: {
            type: Object,
            required: true,
        },
        usersOnZoneToNotify: {
            type: Object,
            required: true,
        },
        areas: {
            type: Object,
            required: true
        },
        groupAlarmsEnabled:{
          type: Boolean,
          default: false,
        },
        editingUserGroupIds: {
          type: Array,
          required: true
        }
    },
    data(){
        return {
            showConfirmModal: false,
            groupIdsSelectedOnZone: [],
        }
    },
    mounted(){
      let selectedGroups = [];
      for(let key in this.groupsOnZoneProp){
        selectedGroups.push(key);
      }
      this.groupIdsSelectedOnZone = selectedGroups;
    },
    methods: {
        askToDelete(){
            // TODO: Confirm that the user can delete this (mass deletion permission)
            this.showConfirmModal = true;
        },
        trash(shouldTrash) {
            if (! shouldTrash) {
                return
            }

            axios.delete(this.company.buildCompanyRoute('zones') + '/' + this.zone.ID).then(response => {
                    window.location.replace(response.request.responseURL);
                }
            ).catch(error => {
            });
        },
        groupEnabledOnZone(groupIdToCheck){
          return this.groupIdsSelectedOnZone.includes(groupIdToCheck.toString());
        },
        groupAlarmsEnabledOnZone(groupIdToCheck){
          if(groupIdToCheck.toString() in this.groupsOnZoneProp){
            return this.groupsOnZoneProp[groupIdToCheck.toString()].pivot.group_alarm_enabled;
          }
          return false;
        },
    },
    computed: {
        metersSelectedOnZone(){
            let selectedMeters = [];
            if(this.zone.hasOwnProperty('meters')){
                this.zone.meters.forEach((thisMeter) => selectedMeters.push(thisMeter.ID));
            }
            return selectedMeters;
        },
        userIdsToNotifyOnZone(){
            let selectedUsers = [];
            for(let key in this.usersOnZoneToNotify){
                selectedUsers.push(key);
            }
            return selectedUsers;
        },
        allGroupsObjectForSelect(){
            let allGroupsObject = {};
            this.allGroups.forEach((thisGroup) => {
                allGroupsObject[thisGroup.ID] = thisGroup.GroupName;
            });
            return allGroupsObject;
        },
        isEditingUserInAnySelectedGroups(){
          let editingUserGroupIds = this.editingUserGroupIds;
          let groupIdsSelectedOnZone = this.groupIdsSelectedOnZone;
          for(let i = 0; i < editingUserGroupIds.length; i++){
            if(groupIdsSelectedOnZone.includes(editingUserGroupIds[i].toString())){
              return true;
            }
          }
          return false;
        }
    }
}
</script>

<style scoped>

</style>